import React, { useState, useEffect } from 'react';
import { 
  Users, Clock, User, Building2, Activity, RefreshCw, 
  Calendar, BarChart3, X, FolderOpen, MapPin, Eye
} from 'lucide-react';
import { formatUtcTimeOnly, formatDateOnly } from '../../../../../utils/timezoneUtils';

const UserReviewModal = ({ user, isOpen, onClose, setError }) => {
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen && user) {
      fetchUserReviewData();
    }
  }, [isOpen, user]);

  const fetchUserReviewData = async () => {
    if (!token || !user) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/labor/user-review/${user.employee_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user review data: ${response.status}`);
      }

      const data = await response.json();
      setReviewData(data);
    } catch (error) {
      console.error('Error fetching user review data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds) return '0h 0m';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="text-xl font-semibold text-slate-200">
                {user?.full_name}
              </h2>
              <p className="text-sm text-slate-400">
                7-Day Review • Employee ID: {user?.employee_id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-slate-400">Loading review data...</div>
            </div>
          ) : reviewData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-slate-400">Total Time</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-200">
                    {formatDuration(reviewData.summary?.total_seconds)}
                  </div>
                </div>
                
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-slate-400">Active Days</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-200">
                    {reviewData.summary?.active_days || 0}
                  </div>
                </div>
                
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-slate-400">Business Units</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-200">
                    {reviewData.summary?.business_units || 0}
                  </div>
                </div>
                
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FolderOpen className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-slate-400">Projects</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-200">
                    {reviewData.summary?.projects || 0}
                  </div>
                </div>
              </div>

              {/* Daily Breakdown */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  Daily Time Breakdown
                </h3>
                <div className="space-y-2">
                  {reviewData.dailyBreakdown?.map((day, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-slate-800/50 rounded">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-300 font-medium min-w-24">
                          {formatDateOnly(day.date, 'America/Chicago')}
                        </span>
                        <span className="text-slate-400 text-sm">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'long' })}
                        </span>
                      </div>
                      <div className="text-slate-200 font-mono">
                        {formatDuration(day.total_seconds)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Units */}
              {reviewData.businessUnits?.length > 0 && (
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-400" />
                    Business Units Worked
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reviewData.businessUnits.map((bu, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-800/50 rounded">
                        <span className="text-slate-300">{bu.business_unit}</span>
                        <span className="text-slate-400 font-mono">
                          {formatDuration(bu.total_seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {reviewData.projects?.length > 0 && (
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-orange-400" />
                    Projects Worked
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reviewData.projects.map((project, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-800/50 rounded">
                        <span className="text-slate-300">{project.project || 'No Project'}</span>
                        <span className="text-slate-400 font-mono">
                          {formatDuration(project.total_seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activities */}
              {reviewData.activities?.length > 0 && (
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-400" />
                    Activities Performed
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reviewData.activities.map((activity, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-800/50 rounded">
                        <span className="text-slate-300">{activity.activity}</span>
                        <span className="text-slate-400 font-mono">
                          {formatDuration(activity.total_seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No review data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UserReviewTab = ({ setError, setSuccess }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const token = localStorage.getItem('token');

  const fetchUsers = async (isRefresh = false) => {
    if (!token) {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      setError(null);
      const response = await fetch('/api/labor/users-with-entries', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds) return '0h 0m';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-medium text-slate-200">
            User Review
          </h3>
          <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">
            {users.length} users
          </span>
        </div>
        <button
          onClick={() => fetchUsers(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Info */}
      <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-200 mb-1">User Time Review</h4>
            <p className="text-sm text-blue-300/80">
              Click on any user tile below to view their detailed 7-day time breakdown including 
              daily hours, business units, projects, and activities worked.
            </p>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      {users.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400">No users with time entries found</p>
          <p className="text-sm text-slate-500 mt-1">
            Users will appear here once they have recorded time entries
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {users.map((user) => (
            <div
              key={user.employee_id}
              onClick={() => handleUserClick(user)}
              className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 hover:bg-slate-800/50 hover:border-slate-600 cursor-pointer transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-200 truncate group-hover:text-white">
                    {user.full_name}
                  </h4>
                  <p className="text-sm text-slate-400 truncate">
                    ID: {user.employee_id}
                  </p>
                </div>
                <Eye className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Time (7d):</span>
                  <span className="text-slate-300 font-mono">
                    {formatDuration(user.total_seconds)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-400">Entries:</span>
                  <span className="text-slate-300">
                    {user.entry_count}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-400">Last Entry:</span>
                  <span className="text-slate-300">
                    {formatDateOnly(user.last_entry, 'America/Chicago')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      <UserReviewModal
        user={selectedUser}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedUser(null);
        }}
        setError={setError}
      />
    </div>
  );
};

export default UserReviewTab;